import { getProductImageURL } from '@noths/polaris-client-utils';
import type {
  ProductCardBadge,
  ProductCarouselItem,
} from '@noths/polaris-design-system-candidate-components';
import type { Product } from '@noths/polaris-design-system-ooux-interfaces';
import type { CurrencyCode } from '@noths/polaris-dev-ts-types';
import { addDays } from 'date-fns';

import type { PlpPreviewProduct } from 'src/services/plpPreview/types';

/**
 * "Most Loved" is the customer-facing name for the `highly_rated` attribute the
 * preview endpoint surfaces as `mostLoved`, matching the PDP's badge rules and
 * the PLP's own cards.
 */
const MOST_LOVED_BADGE: ProductCardBadge = { icon: 'mostLoved', label: 'Most Loved' };

/**
 * Adapts a plp-preview product into the OOUX `Product` the design system consumes.
 *
 * The interface is deliberately semantic, so several service fields have no
 * counterpart and are not mapped: `salePercentage` because `ProductCard` derives
 * the discount by comparing `price` with `originalPrice`, and `onSale` because the
 * discount is the pre-sale price being present. Both still reach GA, which reads
 * the service product directly.
 */
export const transformPlpPreviewProductToProduct = (
  {
    code,
    detailPageHref,
    imageSrc,
    partner,
    preSalePrices,
    price,
    purchasable,
    title,
  }: PlpPreviewProduct,
  { currency }: { currency: CurrencyCode },
): Product => {
  const preSalePrice = preSalePrices?.find((price) => price.currency === currency);

  return {
    /* Derived, not a constant. Nothing filters non-purchasable products out of the
       preview response, and the card this replaces was given `purchasable`
       directly. */
    availability: purchasable ? 'in_stock' : 'out_of_stock',
    code: String(code),
    currencyCode: currency,
    /* The service returns a `{size}` token in place of a preset size, so the URL
       has to be resolved here: `ProductMedia.url` is contracted as a fully
       resolved URL and the card renders it verbatim, unlike the Ribbons card it
       replaces, which resolved the token itself. 'normal' is the size that card
       asked for, so the bytes on the wire do not change.

       Empty alt because the card wraps the image in a link named by the title,
       so describing it again would announce the product twice. */
    media: [{ alt: '', type: 'image', url: getProductImageURL(imageSrc, 'normal') }],
    /* Keyed on the pre-sale price alone, not on `onSale`. That matches the card
       this replaces, which took the pre-sale price and never received `onSale` at
       all, so a product priced down without the flag set keeps showing its
       discount. */
    ...(preSalePrice && { originalPrice: preSalePrice.amount }),
    partner: { name: partner.name, shortcode: partner.shortcode },
    /* The preview endpoint quotes a single price, already in the currency the
       request asked for, unlike the recommendations response's array. */
    price: price.amount,
    title,
    url: detailPageHref,
  };
};

/**
 * The card takes a structured date and cost and words the delivery line itself,
 * so every surface reads identically.
 *
 * Both parts are required, so a product missing either gets no delivery line.
 * Nothing is lost by that: the card drops a line it cannot date anyway. An
 * unpurchasable product is skipped too, since the card shows "Out of stock" in
 * that slot and ignores `delivery` entirely.
 *
 * No `eventName`: the preview contract carries no delivery-by-event data, so the
 * seasonal cut-off wording the card supports has nothing to key on here.
 */
const transformDelivery = (
  { deliveryCost, deliveryDays, purchasable }: PlpPreviewProduct,
  now: Date,
): ProductCarouselItem['delivery'] => {
  if (!purchasable || deliveryCost === null || deliveryDays === null) {
    return undefined;
  }

  return { cost: deliveryCost, date: addDays(now, deliveryDays) };
};

/**
 * The rating worth showing, if any.
 *
 * Gated on the average rather than on `rating` being present, matching the PLP's
 * own `hasRating`. The preview response returns a `ratings` object whose
 * `ratingCount` is not populated, so counting is not an option, and an average of
 * `0` means "unrated" rather than "rated zero" — passing it through would render
 * an empty star row in place of the "NEW" label the card shows when no rating is
 * given.
 */
const transformRating = ({ rating }: PlpPreviewProduct): number | undefined =>
  typeof rating?.average === 'number' && Number.isFinite(rating.average) && rating.average > 0
    ? rating.average
    : undefined;

/**
 * One carousel item is one card's props: the product itself plus the flags that
 * are presentation rather than properties of the product.
 *
 * `priority` is deliberately unset. The carousel is client-fetched behind
 * `LazyRender`, so it is never above the fold and its images should not pre-empt
 * ones that are.
 *
 * `topBrand` is deliberately unmapped. `ProductCard` has a single badge slot,
 * "Most Loved" already claims it, and "Top Brand" describes the partner rather
 * than the product everywhere else in the estate (the PDP's `PartnerBadge`, the
 * PLP's `PartnerPageHeader`), so promoting it to a product badge is a product
 * decision rather than part of this migration.
 */
export const transformPlpPreviewProductToProductCarouselItem = (
  product: PlpPreviewProduct,
  {
    currency,
    /* Injected so that a set of products dates every delivery from the same
       instant, and so that the date is deterministic in tests. */
    now = new Date(),
  }: { currency: CurrencyCode; now?: Date },
): ProductCarouselItem => {
  const delivery = transformDelivery(product, now);
  const rating = transformRating(product);

  return {
    ...(product.mostLoved && { badge: MOST_LOVED_BADGE }),
    ...(delivery && { delivery }),
    /* The card ignores `isNew` once a rating is given, showing the rating in the
       slot the "NEW" label would occupy, so both are passed and the card chooses. */
    isNew: product.isNew,
    product: transformPlpPreviewProductToProduct(product, { currency }),
    /* Constant, not `product.onSale`. Every discount the catalog expresses is a
       price reduction rather than a code-based offer, and the card this replaces
       styled all of them as a sale, so there is no per-product decision to make
       here. `promoteAsSale` does nothing on a product with no discount, so it does
       not need guarding. */
    promoteAsSale: true,
    ...(rating !== undefined && { rating }),
  };
};
